// src/components/YouTubePlayer.jsx
import React, { useEffect, useRef, useState } from "react";

let youtubeApiPromise = null;

function loadYouTubeApi() {
  if (typeof window === "undefined") return Promise.reject(new Error("window unavailable"));
  if (window.YT?.Player) return Promise.resolve();
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof previous === "function") previous();
      } catch {}
      resolve();
    };

    const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existing) return;

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.onerror = () => reject(new Error("failed to load YouTube iframe API"));
    document.body.appendChild(tag);
  });

  return youtubeApiPromise;
}

export default function YouTubePlayer({
  videoId,
  onEnded,
  onPlay,
  onPause,
  className = "",
  showLoadHelp = false,
}) {
  const [isReady, setIsReady] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [showLoadHelpAfterDelay, setShowLoadHelpAfterDelay] = useState(false);
  const mountRef = useRef(null);
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  const onPlayRef = useRef(onPlay);
  const onPauseRef = useRef(onPause);

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);
  useEffect(() => {
    onPlayRef.current = onPlay;
  }, [onPlay]);
  useEffect(() => {
    onPauseRef.current = onPause;
  }, [onPause]);

  useEffect(() => {
    let cancelled = false;
    setIsReady(false);
    setHasLoadError(false);
    setShowLoadHelpAfterDelay(false);
    const loadHelpTimer = window.setTimeout(() => {
      if (!cancelled) setShowLoadHelpAfterDelay(true);
    }, 3500);

    async function initPlayer() {
      if (!mountRef.current) return;
      try {
        await loadYouTubeApi();
        if (cancelled || !mountRef.current) return;

        if (!hostRef.current) {
          hostRef.current = document.createElement("div");
          mountRef.current.appendChild(hostRef.current);
        }

        playerRef.current = new window.YT.Player(hostRef.current, {
          videoId,
          playerVars: {
            rel: 0,
            modestbranding: 1,
            controls: 1,
            fs: 1,
          },
          events: {
            onReady: () => {
              if (!cancelled) setIsReady(true);
            },
            onError: () => {
              if (!cancelled) setHasLoadError(true);
            },
            onStateChange: (e) => {
              if (e?.data === window.YT.PlayerState.PLAYING) {
                try {
                  onPlayRef.current?.();
                } catch {}
              }
              if (e?.data === window.YT.PlayerState.PAUSED) {
                try {
                  onPauseRef.current?.();
                } catch {}
              }
              if (e?.data === window.YT.PlayerState.ENDED) {
                try {
                  onPauseRef.current?.();
                  onEndedRef.current?.();
                } catch {}
              }
            },
          },
        });
      } catch {
        if (!cancelled) setHasLoadError(true);
      }
    }

    initPlayer();

    return () => {
      cancelled = true;
      setIsReady(false);
      try {
        if (playerRef.current?.destroy) playerRef.current.destroy();
      } catch {}
      playerRef.current = null;
      if (hostRef.current?.parentNode) {
        try {
          hostRef.current.parentNode.removeChild(hostRef.current);
        } catch {}
      }
      hostRef.current = null;
      window.clearTimeout(loadHelpTimer);
    };
  }, [videoId]);

  return (
    <div className={`lp-video ${className}`.trim()}>
      {showLoadHelp && (hasLoadError || (!isReady && showLoadHelpAfterDelay)) ? (
        <div className="lp-video-load-help" role="status">
          If the video does not load, please refresh the page.
        </div>
      ) : null}
      <div ref={mountRef} className="lp-video-inner" />
    </div>
  );
}
